import { parsePositionFromString } from '@rio-cloud/rio-uikit/PositionUtils';
import Button from 'components/actions/Button';
import IconButton from 'components/actions/IconButton';
import type { Position } from 'components/map/types';
import { type Dispatch, type SetStateAction, useRef, useState } from 'react';

import PasteWaypointsPanel from './PasteWaypointsPanel';
import type { Waypoint } from './types';

// Rows beyond this are hidden (except the destination) to keep the sidebar usable for large pasted routes
const MAX_ROWS = 50;

const getLabel = (index: number, total: number): string => {
    if (index === 0) return 'Start';
    if (index === total - 1) return 'Dest.';
    return `Via ${index}`;
};

const formatPosition = (position?: Position) =>
    position ? `${position.lat.toFixed(6)}, ${position.lng.toFixed(6)}` : '';

const move = <T,>(items: T[], from: number, to: number): T[] => {
    const result = [...items];
    const [moved] = result.splice(from, 1);
    if (moved !== undefined) result.splice(to, 0, moved);
    return result;
};

type WaypointPanelProps = {
    waypoints: Waypoint[];
    setWaypoints: Dispatch<SetStateAction<Waypoint[]>>;
    onAdd: () => void;
    onUpdate: (id: string, position: Position) => void;
    onPaste: (positions: Position[]) => void;
};

const WaypointPanel = ({ waypoints, setWaypoints, onAdd, onUpdate, onPaste }: WaypointPanelProps) => {
    const [dragOverIndex, setDragOverIndex] = useState<number>();
    const [isPasteOpen, setIsPasteOpen] = useState(false);
    const dragFromIndex = useRef<number>(undefined);

    const total = waypoints.length;
    const isCapped = total > MAX_ROWS;
    const lastWaypoint = waypoints.at(-1);
    const visibleWaypoints = isCapped ? waypoints.slice(0, MAX_ROWS - 1) : waypoints;

    const handleDrop = (toIndex: number) => {
        const fromIndex = dragFromIndex.current;
        if (fromIndex !== undefined && fromIndex !== toIndex) {
            setWaypoints(ws => move(ws, fromIndex, toIndex));
        }
    };

    const handleDragEnd = () => {
        dragFromIndex.current = undefined;
        setDragOverIndex(undefined);
    };

    const renderRow = (waypoint: Waypoint, index: number) => (
        <div
            key={waypoint.id}
            className={`RouteBuilder__waypoint-row${dragOverIndex === index ? ' RouteBuilder__waypoint-row--drag-over' : ''}`}
            onDragOver={e => {
                e.preventDefault();
                setDragOverIndex(index);
            }}
            onDrop={() => handleDrop(index)}
        >
            <span
                className='RouteBuilder__drag-handle rioglyph rioglyph-menu'
                draggable
                onDragStart={() => {
                    dragFromIndex.current = index;
                }}
                onDragEnd={handleDragEnd}
                title='Drag to reorder'
            />
            <span className='RouteBuilder__waypoint-label'>{getLabel(index, total)}</span>
            {/* Uncontrolled, re-mounted via key whenever the position changes elsewhere (e.g. marker drag) */}
            <input
                key={formatPosition(waypoint.position)}
                className='form-control'
                defaultValue={formatPosition(waypoint.position)}
                placeholder='lat, lng'
                onBlur={e => {
                    const position = parsePositionFromString(e.target.value);
                    if (position) onUpdate(waypoint.id, position);
                }}
            />
            <span
                className='rioglyph rioglyph-trash text-16 text-color-danger cursor-pointer flex-shrink-0'
                onClick={() => setWaypoints(ws => ws.filter(w => w.id !== waypoint.id))}
                title='Remove waypoint'
            />
        </div>
    );

    return (
        <div>
            {total === 0 && (
                <p className='text-color-gray margin-bottom-10'>Add waypoints manually or click on the map.</p>
            )}
            <div className='RouteBuilder__waypoint-list'>
                {visibleWaypoints.map(renderRow)}
                {isCapped && lastWaypoint && (
                    <>
                        <div className='RouteBuilder__waypoint-overflow'>··· {total - MAX_ROWS} more (edit on map)</div>
                        {renderRow(lastWaypoint, total - 1)}
                    </>
                )}
            </div>
            <div className='display-flex gap-5 margin-top-10'>
                <Button size='sm' className='flex-1' onClick={() => onAdd()}>
                    <span className='rioglyph rioglyph-plus margin-right-5' />
                    Add waypoint
                </Button>
                {total > 0 && (
                    <IconButton size='sm' onClick={() => setWaypoints([])}>
                        <span className='rioglyph rioglyph-trash' title='Clear all waypoints' />
                    </IconButton>
                )}
            </div>

            <div className='RouteBuilder__paste-toggle'>
                <Button variant='link' size='sm' className='padding-left-0' onClick={() => setIsPasteOpen(v => !v)}>
                    <span className={`rioglyph rioglyph-chevron-${isPasteOpen ? 'up' : 'down'} margin-right-4`} />
                    Paste JSON
                </Button>
                {isPasteOpen && (
                    <div className='margin-top-8'>
                        <PasteWaypointsPanel
                            onLoad={positions => {
                                onPaste(positions);
                                setIsPasteOpen(false);
                            }}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default WaypointPanel;
